import { useRef, useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CoverImageUploadProps {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
}

async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<string> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });

  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 630;
  const ctx = canvas.getContext('2d')!;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    1200,
    630,
  );

  return canvas.toDataURL('image/jpeg', 0.92);
}

export default function CoverImageUpload({ value, onChange }: CoverImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  // Cropper state
  const [rawImage, setRawImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const openCropper = (src: string) => {
    setRawImage(src);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) openCropper(e.target.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const handleRemove = () => {
    onChange(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleCropConfirm = async () => {
    if (!rawImage || !croppedAreaPixels) return;
    const result = await getCroppedImg(rawImage, croppedAreaPixels);
    onChange(result);
    setRawImage(null);
  };

  const handleCropCancel = () => {
    setRawImage(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div>
      <label
        className="block text-sm font-semibold text-zinc-300 mb-2 tracking-wide uppercase"
        style={{ fontFamily: 'Cinzel, serif' }}
      >
        Cover Image{' '}
        <span className="text-zinc-500 text-xs normal-case font-normal ml-1">(optional)</span>
      </label>
      <p className="text-zinc-500 text-xs mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
        Upload a cover image. It will be cropped to 1200 × 630 px (landscape). JPG, PNG or WEBP.
      </p>

      {value && !rawImage ? (
        <div className="relative rounded-lg overflow-hidden border border-zinc-700 group w-full h-56">
          {/* Use the already-cropped dataUrl directly — it is already the correct crop */}
          <img
            src={value}
            alt="Cover preview"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors border border-zinc-600"
              style={{ fontFamily: 'Cinzel, serif' }}
            >
              <i className="ri-image-edit-line"></i>
              Change
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="flex items-center gap-2 px-4 py-2 bg-red-900/60 hover:bg-red-800/70 text-red-300 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors border border-red-800/50"
              style={{ fontFamily: 'Cinzel, serif' }}
            >
              <i className="ri-delete-bin-line"></i>
              Remove
            </button>
          </div>
        </div>
      ) : !rawImage ? (
        <div
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`w-full h-44 flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed transition-all duration-200 cursor-pointer ${
            dragging
              ? 'border-red-600 bg-red-900/10'
              : 'border-zinc-700 bg-zinc-900 hover:border-red-800 hover:bg-zinc-900/80'
          }`}
        >
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-zinc-800 border border-zinc-700">
            <i className="ri-image-add-line text-2xl text-zinc-400"></i>
          </div>
          <div className="text-center">
            <p className="text-zinc-300 text-sm font-medium" style={{ fontFamily: 'Cinzel, serif' }}>
              Click to upload or drag &amp; drop
            </p>
            <p className="text-zinc-600 text-xs mt-1" style={{ fontFamily: 'Crimson Text, serif' }}>
              JPG, PNG, WEBP — max 5MB
            </p>
          </div>
        </div>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleInputChange}
      />

      {/* Crop Modal */}
      {rawImage && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/90">
          {/* Header */}
          <div
            className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-950"
            style={{ fontFamily: 'Cinzel, serif' }}
          >
            <div>
              <h2 className="text-zinc-100 text-base font-semibold tracking-wide">Crop Cover Image</h2>
              <p className="text-zinc-500 text-xs mt-0.5" style={{ fontFamily: 'Crimson Text, serif' }}>
                Drag to reposition · Scroll or use the slider to zoom · Aspect ratio locked to 1200 × 630
              </p>
            </div>
            <button
              type="button"
              onClick={handleCropCancel}
              className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-100 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Cropper area */}
          <div className="relative flex-1">
            <Cropper
              image={rawImage}
              crop={crop}
              zoom={zoom}
              aspect={1200 / 630}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              style={{
                containerStyle: { background: '#09090b' },
                cropAreaStyle: { border: '2px solid #dc2626' },
              }}
            />
          </div>

          {/* Footer controls */}
          <div className="px-6 py-4 border-t border-zinc-800 bg-zinc-950 flex flex-col sm:flex-row items-center gap-4">
            {/* Zoom slider */}
            <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-zoom-out-line text-zinc-400 text-base"></i>
              </div>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 accent-red-600 cursor-pointer"
              />
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-zoom-in-line text-zinc-400 text-base"></i>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleCropCancel}
                className="px-5 py-2 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
                style={{ fontFamily: 'Cinzel, serif' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropConfirm}
                className="px-5 py-2 rounded-md bg-red-700 hover:bg-red-600 text-white text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
                style={{ fontFamily: 'Cinzel, serif' }}
              >
                Apply Crop
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
